/* eslint-disable max-classes-per-file */
import { Expose, instanceToPlain, plainToInstance } from 'class-transformer';
import { IsString } from 'class-validator';
import {
  signInWithEmailAndPassword as _signInWithEmailAndPassword,
  signInWithCustomToken as _signInWithCustomToken,
  sendPasswordResetEmail,
  onAuthStateChanged,
  UserCredential,
  signOut as _signOut,
  getIdTokenResult as _getIdTokenResult,
  IdTokenResult,
  User,
  getAuth,
} from 'firebase/auth';

import { httpsCallable } from 'firebase/functions';

import { auth, functions } from '../../utils/firebase.config';

interface SignInInput {
  email: string;
  password: string;
}

class CreateCustomTokenResource {
  @IsString()
  @Expose({ name: 'profile_id' })
  readonly profileId: string;

  constructor(init?: CreateCustomTokenResource) {
    this.profileId = init?.profileId!;
  }
}

class CreateCustomTokenResult {
  @IsString()
  readonly token: string;

  constructor(init?: CreateCustomTokenResult) {
    this.token = init?.token!;
  }
}

interface UseAuthenticationProps {
  signInWithEmailAndPassword: (input: SignInInput) => Promise<UserCredential>;
  signWithCustomToken: (customToken: string) => Promise<UserCredential>;
  forgotPassword: (email: string) => Promise<void>;
  /** Adds an observer for state change */
  onStateChange: (input: (args?: any) => void) => void;
  signOut: () => Promise<void>;
  createCustomToken: (profileId: string) => Promise<CreateCustomTokenResult>;
  getIdTokenResult: (user: User) => Promise<IdTokenResult> | undefined;
}

export default (): UseAuthenticationProps => {
  const signInWithEmailAndPassword = ({ email, password }: SignInInput) =>
    _signInWithEmailAndPassword(auth, email, password);

  const signWithCustomToken = (customToken: string) => _signInWithCustomToken(auth, customToken);

  const createCustomToken = async (profileId: string) => {
    const input = instanceToPlain(new CreateCustomTokenResource({ profileId }));

    const createToken = httpsCallable<Record<string, unknown>, CreateCustomTokenResult>(functions, 'createCustomToken');
    const result = await createToken(input);

    return plainToInstance(CreateCustomTokenResult, result.data);
  };

  const signOut = () => _signOut(auth);

  const getUser = () => getAuth().currentUser;

  const forgotPassword = (email: string): Promise<void> => sendPasswordResetEmail(auth, email);

  const onStateChange = (input: (args?: any) => void) => onAuthStateChanged(auth, input);

  const getIdTokenResult = () => {
    const user = getUser();

    return user ? _getIdTokenResult(user, true) : undefined;
  };

  return {
    forgotPassword,
    signInWithEmailAndPassword,
    onStateChange,
    signOut,
    createCustomToken,
    signWithCustomToken,
    getIdTokenResult,
  };
};
